/* eslint-disable restrict-imports/restrict-imports */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  makeStyles,
  Typography,
  IconButton,
  CircularProgress,
} from 'design-system';
import { Autocomplete } from '@material-ui/lab';
import Slider from '@mui/material/Slider';
import {
  getCustomColumnConfigs,
  isProjectAdmin,
  setCustomColumnConfigs,
} from '../columnConfigHelpers';
import { useState, useEffect } from 'react';
import { useMount } from 'react-use-v13';
import _ from 'lodash';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  TextField,
} from 'react-scale/components/design-system/product';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  CustomColumn,
  CustomColumnType,
  CustomColumnInputType,
  CustomFormulaColumnInputType,
} from '@scale/shared/types/corp/speedAuditColumnConfig';
import CodeMirror, { EditorDoc } from 'react-scale/corp/components/CodeMirror';
import Chip from '@material-ui/core/Chip';
import CancelIcon from '@material-ui/icons/Cancel';
import useAuditErrorCategories from 'react-scale/components/audits/SpeedAudit/v2/hooks/UseAuditErrorCategories';
import { GridColumns } from '@mui/x-data-grid-pro';

function TextBasedSpeedAuditSettingsModal({
  isOpen,
  setIsOpen,
  cellFontSize,
  setCellFontSize,
  projectId,
  saveColumnConfig,
  columns,
}: {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  cellFontSize: number;
  setCellFontSize: (value: number) => void;
  projectId: string;
  saveColumnConfig: () => void;
  columns: GridColumns;
}) {
  const classes = useStyles();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [editableColumns, setEditableColumns] = useState<CustomColumn[]>([]);
  const [customFormulaColumns, setCustomFormulaColumns] = useState<CustomColumn[]>([]);
  const [fixedCustomColumnsQuery, setFixedCustomColumnsQuery] = useState<EditorDoc>('');
  const [isSavingColumns, setIsSavingColumns] = useState<boolean>(false);
  const [errorCategoriesToRemove, setErrorCategoriesToRemove] = useState<string[]>([]);
  const [newErrorCategory, setNewErrorCategory] = useState<string>('');
  const {
    loading,
    errorCategories: possibleErrorCategories,
    addNewCategory: _addNewCategory,
    removeNewCategory: _removeNewCategory,
  } = useAuditErrorCategories(projectId);

  useMount(async () => {
    const admin = await isProjectAdmin(projectId);
    setIsAdmin(admin);
    const customColumns = await getCustomColumnConfigs(projectId);
    setEditableColumns(customColumns.editableCustomColumns);
    setCustomFormulaColumns(customColumns.customFormulaColumns);
    setFixedCustomColumnsQuery(customColumns.fixedCustomColumnsQuery ?? '');
  });

  const removeNewCategory = (category: string) => {
    setErrorCategoriesToRemove(current => current.concat(category));
    _removeNewCategory(category);
  };

  const addNewCategory = (category: string) => {
    setNewErrorCategory('');
    _addNewCategory(category);
  };

  useEffect(() => {
    setErrorCategoriesToRemove(current =>
      current.filter(category => possibleErrorCategories.includes(category)),
    );
  }, [possibleErrorCategories]);

  const saveCustomColumns = () => {
    // validate editable columns
    setIsSavingColumns(true);
    if (_.some(editableColumns, col => !col.name)) {
      alert('Please name all of the editable columns before saving, or delete them.');
      setIsSavingColumns(false);
      return;
    }
    if (
      _.uniqBy([...editableColumns, ...customFormulaColumns], col => col.name).length !==
      [...editableColumns, ...customFormulaColumns].length
    ) {
      alert('Please make sure all of the columns have different names.');
      setIsSavingColumns(false);
      return;
    }
    if (_.some(customFormulaColumns, col => !col.name)) {
      alert('Please name all of the custom formula columns before saving, or delete them.');
      setIsSavingColumns(false);
      return;
    }

    const success = setCustomColumnConfigs(
      projectId,
      editableColumns,
      customFormulaColumns,
      fixedCustomColumnsQuery.toString(),
    );
    if (!success) {
      alert(
        'Unable to save custom columns configuration. This might be because the admin custom columns config has not yet been created. Please save that before adding any extra columns',
      );
    } else {
      alert('Saved custom column configs.');
    }
    setIsSavingColumns(false);
  };

  const deleteColumn = (index: number, isCustomFormula?: boolean) => {
    (isCustomFormula ? setCustomFormulaColumns : setEditableColumns)(cols => {
      const newCols = _.filter(cols, (_, i) => i !== index);
      return newCols;
    });
  };

  const createColumn = (isCustomFormula?: boolean) => {
    (isCustomFormula ? setCustomFormulaColumns : setEditableColumns)(cols => {
      return [
        ...cols,
        {
          type: CustomColumnType.Standard,
          name: '',
          isEditable: !isCustomFormula,
          inputType: isCustomFormula
            ? CustomFormulaColumnInputType.Difference
            : CustomColumnInputType.Text,
        },
      ];
    });
  };

  const updateColumn = ({
    index,
    type,
    name,
    inputType,
    maxRating,
    selectOptions,
    selectColumns,
    isCustomFormula,
  }: {
    index: number;
    type?: CustomColumnType;
    name?: string;
    inputType?: CustomColumnInputType | CustomFormulaColumnInputType;
    maxRating?: number;
    selectOptions?: string;
    selectColumns?: string[];
    isCustomFormula?: boolean;
  }) => {
    (isCustomFormula ? setCustomFormulaColumns : setEditableColumns)(cols => {
      const col = _.nth(cols, index);
      if (!col) {
        return cols;
      }
      if (type) {
        col.type = type;
      }
      if (name !== undefined) {
        col.name = name;
      }
      if (inputType) {
        col.inputType = inputType;
      }
      if (maxRating) {
        col.maxRating = maxRating;
      }
      if (selectOptions) {
        col.selectOptions = selectOptions;
      }
      if (selectColumns) {
        col.selectColumns = selectColumns;
      }

      return _.concat(cols.slice(0, index), [col], cols.slice(index + 1));
    });
  };

  return (
    <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Settings</DialogTitle>
      <DialogContent className={classes.settingsDialog}>
        <div className={classes.headerRow}>
          <div className={classes.settingsTextGroup}>
            <span className={classes.settingsText}>
              <Typography variant="subtitle1">Font size:</Typography>
            </span>
            <Slider
              className={classes.slider}
              defaultValue={cellFontSize as any}
              min={10}
              max={20}
              step={1}
              marks
              onChange={(e: Event, newVal: number | number[]) =>
                setCellFontSize(_.isNumber(newVal) ? newVal : newVal[0])
              }
            />
          </div>
          <div
            style={{
              fontSize: cellFontSize,
              flexGrow: 1,
              textAlign: 'center',
              alignSelf: 'center',
            }}
          >
            Sample Text
          </div>
        </div>
        {isAdmin && (
          <div className={classes.headerRow}>
            <div className={classes.settingsTextGroup}>
              <span className={classes.settingsText}>
                <Typography variant="subtitle1">
                  Save column configs as default for this project?
                </Typography>
              </span>
            </div>
            <div className={classes.settingsAction}>
              <Button onClick={saveColumnConfig}>Save Admin Configs</Button>
            </div>
          </div>
        )}
        {isAdmin && (
          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Typography variant="subtitle1">
                Add new columns to audits from this project
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div className={classes.addColumnsSection}>
                <Typography variant="subtitle2">Add Editable Columns</Typography>
                {editableColumns?.map((col, i) => {
                  return (
                    <div className={classes.addEditableColumnItem} key={i}>
                      <IconButton onClick={() => deleteColumn(i)}>
                        <FontAwesomeIcon icon="trash-alt" />
                      </IconButton>
                      <TextField
                        value={col.name}
                        label="Name"
                        size="small"
                        variant="outlined"
                        onChange={e => {
                          updateColumn({ index: i, name: e.target.value });
                        }}
                      />
                      <Autocomplete
                        style={{ minWidth: '250px' }}
                        options={Object.values(CustomColumnType)}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            label="Column Type"
                          />
                        )}
                        value={col.type}
                        onChange={(event: any, newValue: CustomColumnType | null) => {
                          updateColumn({ index: i, type: newValue ?? undefined });
                        }}
                      />
                      <Autocomplete
                        style={{ minWidth: '250px' }}
                        options={Object.values(CustomColumnInputType)}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            label="Input Type"
                          />
                        )}
                        value={col.inputType as CustomColumnInputType}
                        onChange={(
                          event: any,
                          newValue: CustomColumnInputType | CustomFormulaColumnInputType | null,
                        ) => {
                          updateColumn({ index: i, inputType: newValue ?? undefined });
                        }}
                      />
                      {col.inputType === CustomColumnInputType.Rating && (
                        <TextField
                          type="number"
                          value={col.maxRating}
                          label="Max Rating"
                          size="small"
                          variant="outlined"
                          onChange={e => {
                            updateColumn({ index: i, maxRating: parseInt(e.target.value) });
                          }}
                        />
                      )}
                      {(col.inputType === CustomColumnInputType.Select ||
                        col.inputType === CustomColumnInputType.MultiSelect) && (
                        <TextField
                          value={col.selectOptions}
                          label="Options"
                          size="small"
                          variant="outlined"
                          onChange={e => {
                            updateColumn({ index: i, selectOptions: e.target.value });
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                <Button
                  className={classes.button}
                  onClick={() => createColumn(false)}
                  variant="outlined"
                >
                  <div className={classes.buttonWithIcon}>
                    <FontAwesomeIcon size="sm" icon="plus" />
                    Add Column
                  </div>
                </Button>
                {/* Set custom formula columns */}
                <Typography variant="subtitle2">Add Custom Formula Columns</Typography>
                {customFormulaColumns?.map((col, i) => {
                  return (
                    <div className={classes.addEditableColumnItem} key={i}>
                      <IconButton onClick={() => deleteColumn(i, true)}>
                        <FontAwesomeIcon icon="trash-alt" />
                      </IconButton>
                      <TextField
                        value={col.name}
                        label="Name"
                        size="small"
                        variant="outlined"
                        onChange={e => {
                          updateColumn({
                            index: i,
                            name: e.target.value,
                            isCustomFormula: true,
                          });
                        }}
                      />
                      <Autocomplete
                        style={{ minWidth: '250px' }}
                        options={Object.values(CustomColumnType)}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            label="Column Type"
                          />
                        )}
                        value={col.type}
                        onChange={(event: any, newValue: CustomColumnType | null) => {
                          updateColumn({
                            index: i,
                            type: newValue ?? undefined,
                            isCustomFormula: true,
                          });
                        }}
                      />
                      <Autocomplete
                        style={{ minWidth: '250px' }}
                        options={Object.values(CustomFormulaColumnInputType)}
                        renderInput={(params: any) => (
                          <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            label="Input Type"
                          />
                        )}
                        value={col.inputType as CustomFormulaColumnInputType}
                        onChange={(
                          event: any,
                          newValue: CustomColumnInputType | CustomFormulaColumnInputType | null,
                        ) => {
                          updateColumn({
                            index: i,
                            inputType: newValue ?? undefined,
                            isCustomFormula: true,
                          });
                        }}
                      />

                      {col.inputType === CustomFormulaColumnInputType.Average && (
                        <Autocomplete
                          style={{ minWidth: '250px' }}
                          options={columns
                            .filter(column => column.type === 'number')
                            .map(column => column.field)}
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="outlined"
                              label="Numeric Columns"
                            />
                          )}
                          value={col.selectColumns}
                          multiple={true}
                          onChange={(event: any, selectColumns: $FIXME) => {
                            updateColumn({
                              index: i,
                              selectColumns: selectColumns ?? undefined,
                              isCustomFormula: true,
                            });
                          }}
                          getOptionLabel={(option: string) => {
                            const column = columns.find(column => column.field === option);
                            if (column && column.headerName) {
                              return column.headerName;
                            }
                            return option;
                          }}
                        />
                      )}

                      {col.inputType === CustomFormulaColumnInputType.Difference && (
                        <Autocomplete
                          style={{ minWidth: '250px' }}
                          options={columns
                            .filter(column => column.type === 'text')
                            .map(column => column.field)}
                          getOptionDisabled={() =>
                            Boolean(col.selectColumns && col.selectColumns.length >= 2)
                          }
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="outlined"
                              label="Text Columns"
                            />
                          )}
                          value={col.selectColumns}
                          multiple={true}
                          onChange={(event: any, selectColumns: $FIXME) => {
                            if (selectColumns.length <= 2) {
                              updateColumn({
                                index: i,
                                selectColumns: selectColumns ?? undefined,
                                isCustomFormula: true,
                              });
                            }
                          }}
                          getOptionLabel={(option: string) => {
                            const column = columns.find(column => column.field === option);
                            if (column && column.headerName) {
                              return column.headerName;
                            }
                            return option;
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                <Button
                  className={classes.button}
                  onClick={() => createColumn(true)}
                  variant="outlined"
                >
                  <div className={classes.buttonWithIcon}>
                    <FontAwesomeIcon size="sm" icon="plus" />
                    Add Column
                  </div>
                </Button>
                <Typography variant="subtitle2">Add Fixed Columns</Typography>
                Add a SQL query to pull extra columns. Every column in the query will be added as a
                column to the table.
                <br />
                - Use '&#123;SUBTASK_IDS&#125;' or '&#123;TASKATTEMPT_IDS&#125;' to filter the query
                to the subtasks/task attempts in this audit . We will replace this text with a list
                of subtask ids.
                <br />
                - This query MUST include 'subtask_id' or 'attempt_id' column so that we can join to
                the existing table on the subtask/attempt!
                <CodeMirror
                  language="sql"
                  value={fixedCustomColumnsQuery}
                  onUpdateValue={(newValue: EditorDoc) => setFixedCustomColumnsQuery(newValue)}
                  className={classes.codeBox}
                />
                <Button className={classes.button} onClick={saveCustomColumns} variant="outlined">
                  {isSavingColumns && (
                    <CircularProgress
                      size={12}
                      color="secondary"
                      thickness={6}
                      className={classes.circularProgress}
                    />
                  )}
                  Save Custom Columns
                </Button>
              </div>
            </AccordionDetails>
          </Accordion>
        )}
        {isAdmin && (
          <Accordion className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Typography variant="subtitle1">Update Error Categories</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div>
                <div className={classes.errorCategories}>
                  {possibleErrorCategories &&
                    possibleErrorCategories
                      .filter(category => !errorCategoriesToRemove.includes(category))
                      .map(category => (
                        <Chip
                          key={category}
                          label={category}
                          className={classes.errorCategory}
                          clickable
                          color="primary"
                          variant="outlined"
                          deleteIcon={
                            loading ? undefined : (
                              <CancelIcon onMouseDown={event => event.stopPropagation()} />
                            )
                          }
                          onDelete={loading ? undefined : () => removeNewCategory(category)}
                        />
                      ))}
                  {loading && (
                    <CircularProgress
                      size={24}
                      color="secondary"
                      thickness={12}
                      className={classes.circularProgress}
                    />
                  )}
                </div>
                {!loading && (
                  <div className={classes.buttonWithIcon}>
                    <TextField
                      value={newErrorCategory}
                      disabled={loading}
                      label="New Error Category"
                      size="small"
                      variant="outlined"
                      onChange={e => {
                        setNewErrorCategory(e.target.value);
                      }}
                    />
                    <IconButton size="small" onClick={() => addNewCategory(newErrorCategory)}>
                      <FontAwesomeIcon icon="plus" />
                    </IconButton>
                  </div>
                )}
              </div>
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles({
  settingsDialog: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  slider: {
    minWidth: 400,
    color: 'rgb(25, 118, 210, 1)',
  },
  settingsTextGroup: {
    display: 'flex',
    width: '75%',
    gap: '24px',
  },
  settingsText: {
    alignSelf: 'center',
    whiteSpace: 'nowrap',
  },
  settingsAction: {
    alignSelf: 'center',
    flexGrow: 1,
    textAlign: 'center',
  },
  accordion: {
    border: 'none',
    boxShadow: 'none',
    position: 'inherit',
  },
  accordionSummary: {
    padding: '0px',
    minHeight: '0px',
    height: '30px',
  },
  errorCategories: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: '15px',
    alignItems: 'center',
  },
  errorCategory: {
    marginTop: 5,
    marginRight: 5,
  },
  buttonWithIcon: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  button: {
    color: 'rgb(25, 118, 210, 1)',
    background: 'white',
    borderColor: 'rgb(25, 118, 210, 1)',
    padding: '6px 16px !important',
    width: 'fit-content',
    '&:hover': {
      background: 'rgb(25, 118, 210, 0.25)',
    },
  },
  addColumnsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  addEditableColumnItem: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center',
  },
  codeBox: {
    border: '1px solid #ccc',
    borderRadius: '8px',
    width: '100%',
    '& .cm-editor': {
      borderRadius: '8px',
      height: '100%',
      maxWidth: '100%',
      overflow: 'auto',
    },
  },
  circularProgress: {
    marginLeft: '8px',
  },
});

export default TextBasedSpeedAuditSettingsModal;
